/*
  Layers favourites. Guest-only, localStorage, no account (customer accounts
  are off on this store). One list per browser.

  Store shape: [{ handle, variantId, title, colour, price, image, url, added }]
  Events: document "layers:favorites" after every change, detail { items }.

  Elements:
    <layers-fav data-handle data-variant-id data-title data-colour data-price data-image data-url>
      a heart button; toggles the item. On a product card it follows the
      card's checked colour swatch so the saved item is the colour shown.
    <layers-fav-count>   the number in the header, hidden at zero
    <layers-favorites>   the page grid; renders from the store, empty state included
*/
const KEY = 'layers-favorites';

const store = {
  read() {
    try {
      const raw = JSON.parse(localStorage.getItem(KEY) || '[]');
      return Array.isArray(raw) ? raw : [];
    } catch (e) {
      return [];
    }
  },
  write(items) {
    try {
      localStorage.setItem(KEY, JSON.stringify(items));
    } catch (e) {
      /* private mode or full: the heart still works for the session */
    }
    document.dispatchEvent(new CustomEvent('layers:favorites', { detail: { items } }));
  },
  key(item) {
    return `${item.handle}::${item.colour || ''}`;
  },
  has(item) {
    const k = this.key(item);
    return this.read().some((i) => this.key(i) === k);
  },
  toggle(item) {
    const items = this.read();
    const k = this.key(item);
    const idx = items.findIndex((i) => this.key(i) === k);
    if (idx > -1) items.splice(idx, 1);
    else items.unshift({ ...item, added: Date.now() });
    this.write(items);
    return idx === -1;
  },
  remove(item) {
    const k = this.key(item);
    this.write(this.read().filter((i) => this.key(i) !== k));
  },
};

window.LayersFavorites = store;

class LayersFav extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button') || this;
    this.button.addEventListener('click', this.onClick);
    this.card = this.closest('product-card');
    this.card?.addEventListener('change', this.onCardChange);
    document.addEventListener('layers:favorites', this.paint);
    this.paint();
  }

  disconnectedCallback() {
    document.removeEventListener('layers:favorites', this.paint);
  }

  get item() {
    const d = this.dataset;
    let colour = d.colour || '';
    let image = d.image || '';
    let url = d.url || '';
    // on a card, follow the colour swatch the visitor picked
    const checked = this.card?.querySelector('swatches-variant-picker-component input[type="radio"]:checked');
    if (checked) {
      colour = checked.value;
      const label = checked.closest('label');
      const media = label?.dataset.mediaId;
      const slide = media && this.card.querySelector(`slideshow-slide[slide-id="${media}"] img`);
      if (slide) image = slide.currentSrc || slide.src || image;
      const vid = checked.dataset.variantId;
      if (vid && url) url = url.split('?')[0] + '?variant=' + vid;
    }
    return { handle: d.handle, variantId: d.variantId || '', title: d.title, colour, price: d.price, image, url };
  }

  onCardChange = (e) => {
    if (e.target?.matches?.('swatches-variant-picker-component input[type="radio"]')) this.paint();
  };

  onClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const on = store.toggle(this.item);
    this.button.setAttribute('aria-pressed', String(on));
    this.classList.toggle('is-on', on);
    this.classList.add('is-pop');
    setTimeout(() => this.classList.remove('is-pop'), 320);
  };

  paint = () => {
    const on = store.has(this.item);
    this.classList.toggle('is-on', on);
    this.button.setAttribute('aria-pressed', String(on));
    this.button.setAttribute('aria-label', on ? 'Remove from favourites' : 'Save to favourites');
    const label = this.querySelector('[data-fav-label]');
    if (label) label.textContent = on ? this.dataset.labelOn || 'Saved' : this.dataset.labelOff || 'Save';
  };
}

class LayersFavCount extends HTMLElement {
  connectedCallback() {
    document.addEventListener('layers:favorites', this.paint);
    this.paint();
  }
  paint = () => {
    const n = store.read().length;
    this.textContent = n > 0 ? String(n) : '';
    this.hidden = n === 0;
    this.closest('a')?.classList.toggle('has-favorites', n > 0);
  };
}

class LayersFavoritesPage extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('[data-fav-grid]');
    this.empty = this.querySelector('[data-fav-empty]');
    this.count = this.querySelector('[data-fav-count]');
    document.addEventListener('layers:favorites', this.render);
    this.render();
  }
  render = () => {
    const items = store.read();
    this.empty.hidden = items.length > 0;
    this.grid.hidden = items.length === 0;
    if (this.count) this.count.textContent = items.length ? `${items.length} saved` : '';
    this.grid.innerHTML = items
      .map(
        (i) => `
      <div class="fav-card">
        <a class="fav-card__media" href="${escapeHtml(i.url)}">
          ${i.image ? `<img src="${escapeHtml(i.image)}" alt="" loading="lazy">` : ''}
        </a>
        <button type="button" class="fav-card__remove" aria-label="Remove ${escapeHtml(i.title)}" data-remove="${escapeHtml(store.key(i))}">
          <svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"><path d="M5 5l10 10M15 5L5 15"/></svg>
        </button>
        <div class="fav-card__meta">
          <a class="fav-card__name" href="${escapeHtml(i.url)}">${escapeHtml(i.title)}${i.colour ? ` · ${escapeHtml(i.colour)}` : ''}</a>
          <span class="fav-card__price">${escapeHtml(i.price || '')}</span>
        </div>
      </div>`
      )
      .join('');
    this.grid.querySelectorAll('[data-remove]').forEach((b) =>
      b.addEventListener('click', () => {
        const item = items.find((i) => store.key(i) === b.dataset.remove);
        if (item) store.remove(item);
      })
    );
  };
}

function escapeHtml(s) {
  return String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

if (!customElements.get('layers-fav')) customElements.define('layers-fav', LayersFav);
if (!customElements.get('layers-fav-count')) customElements.define('layers-fav-count', LayersFavCount);
if (!customElements.get('layers-favorites')) customElements.define('layers-favorites', LayersFavoritesPage);
